import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
    LuDollarSign,
    LuChartBar,
    LuGift,
    LuSave,
    LuTrendingUp,
    LuUser,
    LuMail,
    LuLock,
    LuCircleAlert,
    LuSparkles
} from 'react-icons/lu';

function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { register } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (password !== confirmPassword) {
            setError(t('auth.passwordMismatch'));
            return;
        }

        if (password.length < 6) {
            setError(t('auth.passwordTooShort'));
            return;
        }

        setLoading(true);

        try {
            await register(name, email, password);
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Left Side - Branding */}
                <div className="auth-branding">
                    <div className="auth-branding-content">
                        <div className="auth-branding-logo">
                            <LuDollarSign />
                        </div>
                        <h2>Getiri Hesaplama</h2>
                        <p>Yatırımlarınızı planlayın, hedeflerinize ulaşın. Bileşik faiz hesaplayıcı ile finansal geleceğinizi şekillendirin.</p>

                        <div className="auth-features">
                            <div className="auth-feature">
                                <div className="auth-feature-icon">
                                    <LuGift />
                                </div>
                                <span>7 gün ücretsiz Premium deneme</span>
                            </div>
                            <div className="auth-feature">
                                <div className="auth-feature-icon">
                                    <LuChartBar />
                                </div>
                                <span>Sınırsız hesaplama ve analiz</span>
                            </div>
                            <div className="auth-feature">
                                <div className="auth-feature-icon">
                                    <LuSave />
                                </div>
                                <span>Hesaplamalarınızı kaydedin</span>
                            </div>
                            <div className="auth-feature">
                                <div className="auth-feature-icon">
                                    <LuTrendingUp />
                                </div>
                                <span>Gelişmiş grafikler</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Form */}
                <div className="auth-form-section">
                    <div className="auth-form-wrapper">
                        <div className="auth-header">
                            <h1>{t('auth.register')}</h1>
                            <p>{t('auth.registerSubtitle')}</p>
                        </div>

                        <div className="trial-banner">
                            <LuSparkles className="trial-icon" />
                            <div>
                                <strong>{t('auth.trialTitle')}</strong>
                                <p>{t('auth.trialDescription')}</p>
                            </div>
                        </div>

                        {error && (
                            <div className="auth-error">
                                <LuCircleAlert className="error-icon" />
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="auth-form">
                            <div className="form-group">
                                <label>{t('auth.name')}</label>
                                <div className="input-with-icon">
                                    <LuUser className="input-icon" />
                                    <input
                                        type="text"
                                        className="form-input has-icon"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder={t('auth.namePlaceholder')}
                                        required
                                        autoComplete="name"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('auth.email')}</label>
                                <div className="input-with-icon">
                                    <LuMail className="input-icon" />
                                    <input
                                        type="email"
                                        className="form-input has-icon"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('auth.emailPlaceholder')}
                                        required
                                        autoComplete="email"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('auth.password')}</label>
                                <div className="input-with-icon">
                                    <LuLock className="input-icon" />
                                    <input
                                        type="password"
                                        className="form-input has-icon"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder={t('auth.passwordPlaceholder')}
                                        required
                                        autoComplete="new-password"
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('auth.confirmPassword')}</label>
                                <div className="input-with-icon">
                                    <LuLock className="input-icon" />
                                    <input
                                        type="password"
                                        className="form-input has-icon"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder={t('auth.confirmPasswordPlaceholder')}
                                        required
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-small"></span>
                                        {t('auth.registering')}
                                    </>
                                ) : (
                                    t('auth.registerButton')
                                )}
                            </button>

                            <p className="auth-terms">
                                {t('auth.termsText')}{' '}
                                <Link to="/terms">{t('auth.termsLink')}</Link>
                                {' '}{t('auth.and')}{' '}
                                <Link to="/privacy">{t('auth.privacyLink')}</Link>
                            </p>
                        </form>

                        <div className="auth-divider">
                            <span>{t('auth.or')}</span>
                        </div>

                        <button className="btn-google" disabled aria-label="Google ile kayıt yakında">
                            <svg viewBox="0 0 24 24" width="20" height="20">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Google ile Kayıt Ol (Yakında)
                        </button>

                        <Link to="/demo" className="btn btn-secondary btn-block" style={{ marginTop: '0.75rem' }}>
                            Önce Misafir Olarak Dene
                        </Link>

                        <p className="auth-footer">
                            {t('auth.hasAccount')}{' '}
                            <Link to="/login" className="auth-link">
                                {t('auth.loginLink')}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;
